
import { useEffect, useRef, useState } from "react";
import KanbanBoard from "./KanbanBoard";
import "./timeline.css";
import { ProjectNotes } from "./Projectnotes";
import {
  faEnvelope,
  faPhone,
  faXmark,
  faCalendar,
  faUser,
  faListCheck,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import { TaskDetailModal } from "./TaskDetailModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Info,
  List,
  Kanban,
} from "lucide-react";
import nodata from "../../Utils/assets/img/leads_no_data_found.png";
import {
  Col,
  Row,
  Nav,
  Modal,
  Dropdown,
  Button,
  Form,
  Offcanvas,
  Card,
} from "react-bootstrap";

import ProjectTaskListView from "./Projecttasklistview";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Doughnut, Line } from "react-chartjs-2";

import { TasksOffcanvas } from "./TasksOffcanvas";
import useLocalStorageState from "use-local-storage-state";
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials";
import Projectmeeting from "./Projectmeeting";
import {
  DeleteRequest,
  GetRequest,
  PutRequest,
} from "../../Utils/hooks/ApiHandler";
import { toast } from "sonner";
import {
  formatDate,
  maskContactNumber,
} from "../../Utils/helpers/commonhelper";
import DeleteModal from "../DeleteModal";

import ProjectDetailSkeleton from "./ProjectDetailSkeleton";
import { defaultImage, imageURL } from "../../Utils/constants/constant";
import ProjectDocument from "./Projectdocument";

// Register chart components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
  ChartDataLabels,
);

// Computes total/completed stage and task counts for the summary stat row
const getProjectStageTaskStats = (stages = []) => {
  const totalStages = stages.length;
  let completedStages = 0;
  let totalTasks = 0;
  let completedTasks = 0;

  stages.forEach((stage) => {
    const tasks = stage.tasks || [];
    totalTasks += tasks.length;
    const completedInStage = tasks.filter(
      (task) => task.task_status === "complete",
    ).length;
    completedTasks += completedInStage;
    if (tasks.length > 0 && completedInStage === tasks.length) {
      completedStages += 1;
    }
  });

  return { totalStages, completedStages, totalTasks, completedTasks };
};

// Analytics filter options shown in the "Project Analytics" dropdown
const ANALYTICS_FILTER_OPTIONS = [
  "This Week",
  "This Month",
  "This Quarter",
  "This Year",
];
const getTaskAnalyticsDate = (task) => {
  const rawDate = task.start_date || task.created_at;
  if (!rawDate) return null;
  const parsed = new Date(String(rawDate).replace(" ", "T"));
  return isNaN(parsed.getTime()) ? null : parsed;
};

const buildTaskAnalytics = (stages = [], filterType = "This Year") => {
  const allTasks = [];
  (stages || []).forEach((stage) => {
    (stage.tasks || []).forEach((task) => {
      const date = getTaskAnalyticsDate(task);
      if (date) {
        allTasks.push({ date, completed: task.task_status === "complete" });
      }
    });
  });

  const now = new Date();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  let labels = [];
  let buckets = [];
  if (filterType === "This Week") {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const startOfWeek = new Date(now);
    startOfWeek.setHours(0, 0, 0, 0);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    labels = dayNames;
    buckets = dayNames.map((_, i) => {
      const start = new Date(startOfWeek);
      start.setDate(startOfWeek.getDate() + i);
      const end = new Date(start);
      end.setDate(start.getDate() + 1);
      return { start, end };
    });
  } else if (filterType === "This Month") {
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weekCount = Math.ceil((daysInMonth + firstDay.getDay()) / 7);
    labels = Array.from({ length: weekCount }, (_, i) => `Week ${i + 1}`);
    buckets = Array.from({ length: weekCount }, (_, i) => {
      const start = new Date(year, month, 1 + i * 7 - firstDay.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 7);
      return { start, end };
    });
  } else if (filterType === "This Quarter") {
    const currentMonth = now.getMonth();
    const quarterStartMonth = currentMonth - (currentMonth % 3);
    labels = [0, 1, 2].map((i) => monthNames[quarterStartMonth + i]);
    buckets = [0, 1, 2].map((i) => {
      const start = new Date(now.getFullYear(), quarterStartMonth + i, 1);
      const end = new Date(now.getFullYear(), quarterStartMonth + i + 1, 1);
      return { start, end };
    });
  } else {
    labels = monthNames;
    buckets = monthNames.map((_, i) => {
      const start = new Date(now.getFullYear(), i, 1);
      const end = new Date(now.getFullYear(), i + 1, 1);
      return { start, end };
    });
  }
  const totalByBucket = Array(buckets.length).fill(0);
  const completedByBucket = Array(buckets.length).fill(0);
  allTasks.forEach(({ date, completed }) => {
    const idx = buckets.findIndex((b) => date >= b.start && date < b.end);
    if (idx === -1) return;
    totalByBucket[idx] += 1;
    if (completed) completedByBucket[idx] += 1;
  });

  return { labels, totalByBucket, completedByBucket };
};
const truncateWords = (text, maxWords = 100) => {
  if (!text) return "";
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(" ")}...`;
};

const parseUserPhone = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const ProjectDetails = ({
  show,
  onClose,
  projectIdForDetailPage,
  refetch,
  moduleName,
  fetchProjectList,
}) => {
  const { customer, headers, extras, customerId, loginUserId } = useGetEssentials();
  const [PERMISSION, _] = useLocalStorageState("permission");
  const [taskDetailShow, setTaskDetailShow] = useState(false);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  const [activeTab, setActiveTab] = useState("project-details");
  const [taskShow, setTaskShow] = useState(false);
  const [projectData, setProjectData] = useState([]);
  const [addIndivisbleTask, setAddIndivisualTask] = useState(false);
  const [editTaskData, setEditTaskData] = useState([]);
  const [projectId, setProjectId] = useState(null);


  const [stageData, setStageData] = useState([]);

  const [fetchTaskToken, setFetchTaskToken] = useState(false);

  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [completeTaskClicked, setTaskCompleteClick] = useState(false);
  const [cancleTaskClicked, setTaskCancleClick] = useState(false);
  const [inCompleteTaskClicked, setTaskInCompleteClick] = useState(false);
  const [toChangeTaskStatus, setToChangeTaskStatus] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [analyticsFilter, setAnalyticsFilter] = useState("This Year");

  // scroll back to ckeditor
  const notesRef = useRef(null);
  if (notesRef.current) {
    notesRef.current.scrollIntoView({ behavior: "smooth" });
  }
  // permission setup for purchase order
  const projectGrant = PERMISSION?.project;
  const isProject = projectGrant?.isAuthorized;
  const taskGrant = projectGrant?.feature?.task;
  const meetingGrant = projectGrant?.feature?.meeting;
  const noteGrant = projectGrant?.feature?.notes;
  const docGrant = projectGrant?.feature?.document;
  const handleCloseModal = () => {
    setShowConfirm(false);
    setRejectReason("");
    setLoading(false);
    setTaskCompleteClick(false);
    setTaskCancleClick(false);
    setTaskInCompleteClick(false);
  };


  const fetchDetailsProjects = async () => {
    if (!projectIdForDetailPage) {
      return;
    }
    try {
      setIsLoading(true);
      const res = await GetRequest(
        `/project/fetchDetailsProjects/${customerId}/${projectIdForDetailPage}/${loginUserId}`,
        { headers },
        extras,
      );
      if (res.status === "fail" || res.status !== "success") {
        return toast.error(res.message);
      }

      setProjectData(res.data[0]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (projectIdForDetailPage) {
      fetchDetailsProjects();
    }
  }, [customerId, projectIdForDetailPage, fetchTaskToken]);

  const fetchStages = async () => {
    setStageData([]);
    try {
      const res = await GetRequest(
        `project/getProgressOfStagesTask/${customerId}/${projectData?.id}`,
        { headers },
        extras,
      );

      if (res.status !== "success") return;

      setStageData(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchStages();
  }, [projectData?.id, fetchTaskToken, customerId]);
  const phoneData = parseUserPhone(projectData?.user_phone);
  const firstPhone = phoneData[0]?.phone;
  const phoneCountryCode = phoneData[0]?.country_code;

  const handleOnCloseClick = async () => {
    setProjectData([]);
    setProjectId(null);
    setStageData([]);
    onClose();
    if (moduleName === "ProjectDashboard") {
      await refetch();
    }
  };


  return (
    <>
      <>
        <Offcanvas
          className="w-100 d-flex"
          show={show}
          enforceFocus={false}
          // backdrop="static"
          onHide={handleOnCloseClick}
          placement="end"
          style={{ padding: "10px" }}
        // id="contactDetailsOffcanvas"
        >
          {isLoading ? (
            <ProjectDetailSkeleton />
          ) : (
            <>
              <Offcanvas.Header
                style={{
                  background: "#f3f3f3",
                  borderBottom: "none",
                  paddingBottom: "0",
                }}
              >
                <div
                  className="w-100 d-flex align-items-center justify-content-between fs-13"
                  style={{ padding: "2px 7px 13px", color: "#0081d7" }}
                >
                  <div className="d-flex align-items-center gap-1 navForTasks">
                    <Nav
                      variant="pills"
                      className="nav-style-1 mb-0 d-flex flex-nowrap gap-2"
                    >
                      <Nav.Item className="me-0">
                        <Nav.Link
                          active={activeTab === "project-details"}
                          className="p-2 py-2 pt-3"
                          style={styles.navLinks}
                          onClick={() => setActiveTab("project-details")}
                        >
                          <Info size={13} className="me-1" />{" "}
                          <span>Summary</span>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item className="me-0">
                        <Nav.Link
                          active={activeTab === "list"}
                          className="p-2 py-2 pt-3"
                          style={styles.navLinks}
                          onClick={() => {
                            if (!taskGrant?.isView)
                              return toast.warning(
                                "You are not authorized for this action!",
                              );
                            setActiveTab("list");
                          }}
                        >
                          <List size={13} className="me-1" /> <span>List</span>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item className="me-0">
                        <Nav.Link
                          active={activeTab === "board"}
                          className="p-2 py-2 pt-3"
                          style={styles.navLinks}
                          onClick={() => {
                            if (!taskGrant?.isView)
                              return toast.warning(
                                "You are not authorized for this action!",
                              );
                            setActiveTab("board");
                          }}
                        >
                          <Kanban size={13} className="me-1" />{" "}
                          <span>Board</span>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item className="me-0">
                        <Nav.Link
                          active={activeTab === "meetings"}
                          className="p-2 py-2 pt-3"
                          style={styles.navLinks}
                          onClick={() => {
                            if (!meetingGrant?.isView) return;
                            setActiveTab("meetings");
                          }}
                        >
                          <Kanban size={13} className="me-1" />{" "}
                          <span>Meeting</span>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item className="me-0">
                        <Nav.Link
                          active={activeTab === "notes"}
                          className="p-2 py-2 pt-3"
                          style={styles.navLinks}
                          onClick={() => {
                            if (!noteGrant?.isView) return;
                            setActiveTab("notes");
                          }}
                        >
                          <Kanban size={13} className="me-1" />{" "}
                          <span>Notes</span>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item className="me-0">
                        <Nav.Link
                          active={activeTab === "document"}
                          className="p-2 py-2 pt-3"
                          style={styles.navLinks}
                          onClick={() => {
                            if (!docGrant?.isView) return;
                            setActiveTab("document");
                          }}
                        >
                          <Kanban size={13} className="me-1" />{" "}
                          <span>Document</span>
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </div>
                  <div>
                    <div className="d-flex align-items-center gap-1">
                      <Button
                        variant="default"
                        className="btnss"
                        style={styles.listButtonsStyles}
                        onClick={() => {
                          handleOnCloseClick();
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} className="ms-0" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Offcanvas.Header>

              <Offcanvas.Body
                className="pt-0"
                style={{ background: "#f3f3f3" }}
              >
                <Row
                  className="ms-0 h-100 me-0"
                  style={{
                    display: activeTab === "project-details" ? "flex" : "none",
                  }}
                >
                  <Col
                    md={6}
                    className="ps-0 contactInformation"
                    style={{
                      height: "100%",
                      paddingRight: 0,
                      overflowY: "auto",
                      overflowX: "hidden",
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Card
                      className=" my-card"
                      style={{
                        paddingTop: "12px",
                        borderRadius: "5px",
                        border: "1px solid #e0e4f8",
                        marginBottom: "10px",
                      }}
                    >
                      <style>
                        {`
                          .my-card {
                            padding-top: 12px !important;
                            border-radius: 5px;
                            border: 1px solid #e0e4f8;
                            margin-bottom: 2px !important;
                       
                          }
                        `}
                      </style>
                      {/* <Card.Body className="p-3"> */}
                      <div
                        className="d-flex gap-3 align-items-center mb-0  pt-2 pb-0"
                        style={styles.stickyHeader}
                      >
                        <div
                          style={{
                            color: "#576f91",
                            fontSize: "13px",
                            fontWeight: 500,
                            width: "100%",
                          }}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <h4
                              className="mb-0"
                              style={{
                                fontSize: "20px",
                                fontWeight: "500",
                                color: "#3c3c3c",
                              }}
                            >
                              {projectData?.project_title}
                            </h4>
                            <p
                              className="mt-0 fs-13"
                              style={{
                                color: "rgb(176, 186, 201)",
                                fontWeight: 300,
                              }}
                            >
                              {truncateWords(projectData?.description, 20)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <hr
                        style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
                      />
                      <div
                        className="pt-0 ps-0 pb-0 mt-2"
                        style={{
                          background: "transparent",
                        }}
                      >
                        <Row className="w-100 ms-0 mb-0 ">
                          <Col
                            md={4}
                            className="mb-3 d-flex align-items-center"
                          >
                            <FontAwesomeIcon
                              icon={faUser}
                              style={styles.iconsStyles}
                              className=" fs-16"
                            />
                            <div>
                              <p className="mb-0" style={styles.labels}>
                                Project Owner
                              </p>
                              <p className="mb-0 " style={styles.values}>
                                {projectData.owner_name}
                              </p>
                            </div>
                          </Col>
                          <Col
                            md={4}
                            className="mb-3 d-flex align-items-center"
                          >
                            <FontAwesomeIcon
                              icon={faPhone}
                              style={styles.iconsStyles}
                              className=" fs-16"
                            />
                            <div>
                              <p className="mb-0" style={styles.labels}>
                                Contact No.
                              </p>
                              <p className="mb-0" style={styles.values}>
                                {/* + {phoneCountryCode} {` ${firstPhone}`} */}
                                {firstPhone
                                  ? `+${phoneCountryCode} ${maskContactNumber(customer, firstPhone)}`
                                  : "-"}
                              </p>
                            </div>
                          </Col>
                          <Col
                            md={4}
                            className="mb-3 d-flex align-items-center"
                          >
                            <FontAwesomeIcon
                              icon={faEnvelope}
                              className=" fs-16"
                              style={styles.iconsStyles}
                            />
                            <div>
                              <p className="mb-0" style={styles.labels}>
                                Email
                              </p>
                              <div className="mb-0 " style={styles.values}>
                                <span
                                  title={projectData?.email}
                                  style={{
                                    color: "#0099ff",
                                    cursor: "pointer",
                                    marginLeft: "0px",
                                  }}
                                >
                                  {projectData?.email?.length > 20
                                    ? `${projectData.email.slice(0, 20)}...`
                                    : projectData?.email}
                                </span>
                              </div>
                            </div>
                          </Col>
                          <Col
                            md={4}
                            className="mb-3 d-flex align-items-center"
                          >
                            <FontAwesomeIcon
                              icon={faCalendar}
                              style={styles.iconsStyles}
                              className=" fs-16"
                            />
                            <div>
                              <p className="mb-0" style={styles.labels}>
                                Start Date
                              </p>
                              <p className="mb-0" style={styles.values}>
                                {formatDate(projectData?.start_date)}
                              </p>
                            </div>
                          </Col>
                          <Col
                            md={4}
                            className="mb-3 d-flex align-items-center"
                          >
                            <FontAwesomeIcon
                              icon={faCalendar}
                              style={styles.iconsStyles}
                              className=" fs-16"
                            />
                            <div>
                              <p className="mb-0" style={styles.labels}>
                                End Date
                              </p>
                              <p className="mb-0" style={styles.values}>
                                {formatDate(projectData?.end_date)}
                              </p>
                            </div>
                          </Col>
                          {projectData?.project_status === "complete" && (
                            <Col
                              md={4}
                              className="mb-3 d-flex align-items-center"
                            >
                              <FontAwesomeIcon
                                icon={faCalendar}
                                style={styles.iconsStyles}
                                className=" fs-16"
                              />
                              <div>
                                <p className="mb-0" style={styles.labels}>
                                  Completion Date
                                </p>
                                <p className="mb-0" style={styles.values}>
                                  {formatDate(projectData?.completion_date)}
                                </p>
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                      {/* </Card.Body> */}
                    </Card>
                    <Row
                      className="w-100 ms-0 ps-0  mt-2"
                      style={{ flex: 1, minHeight: 0 }}
                    >
                      <Col
                        md={6}
                        className=" ps-0 pe-0"
                        style={{ height: "100%" }}
                      >
                        <Card
                          style={{
                            background: "#fff",
                            paddingTop: "6px",
                            borderRadius: "5px",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Card.Header
                            className="pb-1"
                            style={{
                              marginLeft: "-1.2rem",
                              marginRight: "-1.2rem",
                              flexShrink: 0,
                            }}
                          >
                            <div
                              className="pb-2"
                              style={{ fontWeight: "300", fontSize: "1rem" }}
                            >
                              Associated Contact
                            </div>
                          </Card.Header>
                          <div
                            style={{
                              flex: 1,
                              minHeight: 0,
                              overflowY: "auto",
                              overflowX: "hidden",
                              scrollbarWidth: "thin",
                            }}
                          >
                            {/* <Card.Body className="pt-3"> */}
                            {projectData?.associated_owners?.length > 0 ? (
                              projectData?.associated_owners?.map(
                                (item, idx) => (
                                  <>
                                    <div
                                      className="d-flex gap-2 align-items-center justify-content-between pt-3  "
                                      style={{
                                        width: "-webkit-fill-available",
                                      }}
                                    >
                                      {console.log(projectData, "tata")}
                                      <div
                                        className=" p-0"
                                        style={{
                                          width: 40,
                                          borderRadius: "50%",
                                        }}
                                      >
                                        <img
                                          src={
                                            item?.profile_image &&
                                              item?.profile_image !== "null"
                                              ? `${imageURL}${item.profile_image}`
                                              : defaultImage
                                          }
                                          alt=""
                                          style={{
                                            width: "40px",
                                            height: "40px",
                                            borderRadius: "50%",
                                          }}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          width: "-webkit-fill-available",
                                        }}
                                      >
                                        <div className="d-flex justify-content-between align-item-center ">
                                          <div className="d-flex flex-column">
                                            <p
                                              className="d-flex align-tems-center justify-content-between text-dark"
                                              style={{
                                                fontWeight: 600,
                                                textWrap: "nowrap",
                                              }}
                                            >
                                              {item?.owner_name}{" "}
                                            </p>
                                            <p
                                              style={{ fontWeight: 300 }}
                                              className="fs-11"
                                            >
                                              {item?.designation_name ||
                                                "Admin"}
                                            </p>
                                          </div>
                                          {/* <div>
                                  <Badge
                                    bg="warning"
                                    className="myBadgeP"
                                    style={{
                                      fontWeight: 300,
                                      borderRadius: "20px",
                                      marginTop: "10px"
                                    }}
                                  >
                                    Implementation
                                  </Badge>{" "}
                                  <style>
                                    {`
                                      .myBadgeP {
                                        fontWeight: 300 !important;
                                        border-radius: 20px;
                                        color: #000;
                                        marginTop:10px !important;
                                      }
                                    `}
                                  </style>
                                </div>{" "} */}
                                          <div>
                                            <span
                                              className="fs-12"
                                              style={{
                                                color: "#a7a7a7",
                                                fontWeight: 300,
                                              }}
                                            >
                                              Task :{" "}
                                            </span>
                                            <span className="fs-13">
                                              {item?.complete_task}/
                                              {item?.total_task}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <hr />
                                  </>
                                ),
                              )
                            ) : (
                              <td
                                colSpan={6}
                                style={{ textAlign: "center", color: "gray" }}
                              >
                                <div className="d-flex justify-content-center align-items-center">
                                  <img
                                    src={nodata}
                                    alt=""
                                    style={{ width: "17rem", marginTop: "0" }}
                                  />
                                </div>
                              </td>
                            )}

                            {/* </Card.Body> */}
                          </div>
                        </Card>
                      </Col>

                      <Col md={6} className="pe-0" style={{ height: "100%" }}>
                        <Card
                          style={{
                            background: "#fff",
                            paddingTop: "6px",
                            borderRadius: "5px",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Card.Header
                            className="pb-1"
                            style={{
                              marginLeft: "-1.2rem",
                              marginRight: "-1.2rem",
                              flexShrink: 0,
                            }}
                          >
                            <div
                              className="pb-2"
                              style={{ fontWeight: "300", fontSize: "1rem" }}
                            >
                              Project Progress
                            </div>
                          </Card.Header>
                          <Card.Body
                            className=""
                            style={{ flex: 1, minHeight: 0, width: "100%" }}
                          >
                            {(() => {
                              const stages = stageData?.[0]?.stages || [];

                              const stageNames = stages.map(
                                (stage) => stage.stage_name || "Unknown Stage",
                              );
                              const values = stages.map((stage) =>
                                stage.progress
                                  ? Number(stage?.progress?.toFixed(0))
                                  : 0,
                              );

                              const colors = [
                                "#3b82f6", // Blue
                                "#10b981", // Green
                                "#f59e0b", // Amber
                                "#ef4444", // Red
                                "#8b5cf6", // Purple
                                "#06b6d4", // Cyan
                                "#84cc16", // Lime
                              ];

                              const backgroundColors = values.map(
                                (value, index) =>
                                  value === 0
                                    ? "transparent"
                                    : colors[index % colors.length],
                              );

                              const borderColors = values.map((value, index) =>
                                value === 0
                                  ? "#9ca3af"
                                  : colors[index % colors.length],
                              );

                              const data = {
                                labels: stageNames,
                                datasets: [
                                  {
                                    data: values,
                                    backgroundColor: backgroundColors,
                                    borderColor: borderColors,
                                    borderWidth: 2,
                                    borderRadius: 3,
                                    barPercentage: 0.8,
                                    categoryPercentage: 0.9,
                                    minBarLength: 0,
                                  },
                                ],
                              };

                              const options = {
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: { display: false },
                                  tooltip: {
                                    enabled: true,
                                    backgroundColor: "#ffffff",
                                    titleColor: "#000000",
                                    bodyColor: "#000000",
                                    borderColor: "#e5e7eb",
                                    borderWidth: 1,
                                    titleFont: { size: 12, weight: "bold" },
                                    bodyFont: { size: 12 },
                                    callbacks: {
                                      label: (context) =>
                                        `${stageNames[context.dataIndex]}: ${context.raw
                                        }%`,
                                    },
                                  },
                                  datalabels: {
                                    display: false,
                                  },
                                },
                                scales: {
                                  x: {
                                    ticks: {
                                      display: true,
                                      color: "#4b5563",
                                      font: { size: 11, weight: "500" },
                                      autoSkip: false,
                                      maxRotation: 45,
                                      minRotation: 0,
                                      callback: function (value, index) {
                                        const label =
                                          this.getLabelForValue(value);
                                        return label.length > 30
                                          ? label.slice(0, 30) + "…"
                                          : label;
                                      },
                                    },
                                    grid: {
                                      display: false,
                                      drawBorder: false,
                                    },
                                  },
                                  y: {
                                    beginAtZero: true,
                                    max: 100,
                                    ticks: {
                                      stepSize: 25,
                                      callback: (val) => `${val?.toFixed(0)}%`,
                                      color: "#6b7280",
                                      font: { size: 11, weight: "500" },
                                    },
                                    grid: {
                                      color: "#f3f4f6",
                                      drawBorder: false,
                                    },
                                  },
                                },
                              };

                              return <Bar data={data} options={options} />;
                            })()}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Col>
                  <Col
                    md={6}
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                    className="pt-0"
                  >
                    {/* Section 1: Stat cards */}
                    <div
                      className="cardBorders"
                      style={{
                        borderRadius: "5px",
                        padding: "0px",
                        border: "1px solid #e0e4f8",
                        flexShrink: 0,
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          padding: "14px 18px",
                        }}
                      >
                        {/* Stat row */}
                        {(() => {
                          const {
                            totalStages,
                            completedStages,
                            totalTasks,
                            completedTasks,
                          } = getProjectStageTaskStats(
                            projectData?.stages || [],
                          );

                          return (
                            <>
                              <style>{` 
                            .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 1.5rem;
          }

          @media (max-width: 768px) {
            .stats-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: 1rem;
            }
          }

          @media (max-width: 480px) {
            .stats-grid {
              grid-template-columns: 1fr;
            }
          }
                      /* Stat Cards */
          .stat-card {
            background: white;
            border:1px solid #caeaf4;
            border-radius: 12px;
            padding: 1.5rem;
            font-size:11px;
            font-weight:400;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          }
            
          .stat-title {
            font-size: 0.875rem;
            font-weight: 500;
            color: #64748b;
            margin-bottom: 0.75rem;
            letter-spacing: 0.3px;
          }

          .stat-value {
            font-size: 1.875rem;
            font-weight: 700;
            color: #1e293b;
            margin-bottom: 0.75rem;
          }

          .stat-badge {
            font-size: 0.75rem;
            font-weight: 600;
            padding: 0.375rem 0.75rem;
            border-radius: 6px;
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            border: 1px solid;
          }

          .stat-icon-wrapper {
            width: 48px;
            height: 48px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
                `}</style>
                              <div className="stats-grid">
                                <div className="stat-card">
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <p className="stat-title">Total Stages</p>
                                      <h3 className="stat-value">
                                        {totalStages}
                                      </h3>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0.5rem",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: "0.8rem",
                                            color: "#64748b",
                                          }}
                                        >
                                          complated stages :
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 11,
                                            color: "#10b981",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {completedStages}
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      className="stat-icon-wrapper"
                                      // style={{ backgroundColor: "#d1fae5" }}
                                      style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "8px",
                                        background: "#eef2ff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#4f46e5",
                                        fontSize: 14,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faLayerGroup} />
                                    </div>
                                  </div>
                                </div>
                                <div className="stat-card">
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <p className="stat-title">Total Task</p>
                                      <h3 className="stat-value">
                                        {totalTasks}
                                      </h3>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0.5rem",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: "0.8rem",
                                            color: "#64748b",
                                          }}
                                        >
                                          completed task :
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 11,
                                            color: "#10b981",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {completedTasks}
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      className="stat-icon-wrapper"
                                      style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "8px",
                                        background: "#e6f7f0",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#10b981",
                                        fontSize: 14,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faListCheck} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Section 2: Analytics graph */}
                    <div
                      className="cardBorders"
                      style={{
                        borderRadius: "5px",
                        padding: "0px",
                        border: "1px solid #e0e4f8",
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                          minHeight: 0,
                          padding: "14px 18px",
                        }}
                      >
                        {/* Analytics chart */}
                        <div
                          style={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div
                            className="d-flex align-items-start justify-content-between"
                            style={{ flexShrink: 0, marginBottom: 4 }}
                          >
                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 14,
                                  color: "#1a1a1e",
                                }}
                              >
                                Project Analytics
                              </div>
                              <div style={{ fontSize: 11, color: "#8590a2" }}>
                                Task completion and project progress over time
                              </div>
                            </div>
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                variant="light"
                                style={{
                                  fontSize: 12,
                                  padding: "4px 10px",
                                  border: "1px solid #e5e7eb",
                                  background: "#fff",
                                  color: "#4b5563",
                                  boxShadow: "none",
                                }}
                              >
                                {analyticsFilter}
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                {ANALYTICS_FILTER_OPTIONS.map((option) => (
                                  <Dropdown.Item
                                    key={option}
                                    active={option === analyticsFilter}
                                    onClick={() => setAnalyticsFilter(option)}
                                    style={{ fontSize: 13 }}
                                  >
                                    {option}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                          <div style={{ flex: 1, minHeight: 0, width: "100%" }}>
                            {(() => {
                              const {
                                labels,
                                totalByBucket,
                                completedByBucket,
                              } = buildTaskAnalytics(
                                projectData?.stages || [],
                                analyticsFilter,
                              );

                              const data = {
                                labels,
                                datasets: [
                                  {
                                    label: "Total Tasks",
                                    data: totalByBucket,
                                    borderColor: "#7c6ff2",
                                    backgroundColor:
                                      "rgba(124, 111, 242, 0.18)",
                                    fill: true,
                                    tension: 0.45,
                                    borderWidth: 2.5,
                                    pointRadius: 0,
                                    pointHoverRadius: 5,
                                    pointHoverBackgroundColor: "#7c6ff2",
                                    pointHoverBorderColor: "#fff",
                                    pointHoverBorderWidth: 2,
                                    cubicInterpolationMode: "monotone",
                                  },
                                  {
                                    label: "Completed",
                                    data: completedByBucket,
                                    borderColor: "#34c38f",
                                    backgroundColor: "rgba(52, 195, 143, 0.18)",
                                    fill: true,
                                    tension: 0.45,
                                    borderWidth: 2.5,
                                    pointRadius: 0,
                                    pointHoverRadius: 5,
                                    pointHoverBackgroundColor: "#34c38f",
                                    pointHoverBorderColor: "#fff",
                                    pointHoverBorderWidth: 2,
                                    cubicInterpolationMode: "monotone",
                                  },
                                ],
                              };

                              const options = {
                                responsive: true,
                                maintainAspectRatio: false,
                                interaction: {
                                  mode: "index",
                                  intersect: false,
                                },
                                plugins: {
                                  legend: {
                                    position: "top",
                                    align: "end",
                                    labels: {
                                      boxWidth: 8,
                                      boxHeight: 8,
                                      usePointStyle: true,
                                      pointStyle: "circle",
                                      font: { size: 11 },
                                      color: "#4b5563",
                                      padding: 12,
                                    },
                                  },
                                  tooltip: {
                                    enabled: true,
                                    backgroundColor: "#fff",
                                    titleColor: "#1a1a1e",
                                    bodyColor: "#1a1a1e",
                                    borderColor: "#e5e7eb",
                                    borderWidth: 1,
                                    padding: 10,
                                    boxPadding: 4,
                                  },
                                  datalabels: { display: false },
                                },
                                scales: {
                                  x: {
                                    grid: { display: false },
                                    border: { display: false },
                                    ticks: {
                                      font: { size: 10.5 },
                                      color: "#9ca3af",
                                    },
                                  },
                                  y: {
                                    beginAtZero: true,
                                    border: { display: false },
                                    ticks: {
                                      precision: 0,
                                      font: { size: 10.5 },
                                      color: "#9ca3af",
                                      maxTicksLimit: 5,
                                    },
                                    grid: {
                                      color: "#f1f2f6",
                                      drawBorder: false,
                                      borderDash: [4, 4],
                                    },
                                  },
                                },
                              };

                              return <Line data={data} options={options} />;
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                {activeTab === "list" && (
                  <ProjectTaskListView
                    projectData={projectData}
                    project_id={projectData?.id}
                    headers={headers}
                    extras={extras}
                    customer={customer}
                    customerId={customerId}
                    onOpenTask={(task) => {
                      setSelectedTaskDetail(task);
                      setTaskDetailShow(true);
                    }}
                    onDataChange={() => {
                      fetchDetailsProjects();
                      fetchStages();
                    }}
                    taskGrant={taskGrant}
                  />
                )}

                {activeTab === "board" && (
                  <KanbanBoard
                    projectData={projectData}
                    headers={headers}
                    extras={extras}
                    customerId={customerId}
                    customer={customer}
                    taskGrant={taskGrant}
                    onTaskClick={(task) => {
                      setSelectedTaskDetail(task);
                      setTaskDetailShow(true);
                    }}
                    onDataChange={() => {
                      fetchDetailsProjects();
                      fetchStages();
                    }}
                    fetchProjectList={fetchProjectList}
                    refetch={() => {
                      fetchDetailsProjects();
                      fetchStages();
                    }}
                  />
                )}
                {activeTab === "meetings" && (
                  <Projectmeeting
                    leadId={projectData?.id}
                    email={projectData?.email}
                    fullName={projectData?.contact_name}
                    module_name="project"
                    meetingGrant={meetingGrant}
                    projectData={projectData}
                    headers={headers}
                    extras={extras}
                    customerId={customerId}
                    customer={customer}
                    onTaskClick={(task) => {
                      setSelectedTaskDetail(task);
                      setTaskDetailShow(true);
                    }}
                    onDataChange={() => {
                      fetchDetailsProjects();
                      fetchStages();
                    }}
                  />
                )}
                {activeTab === "notes" && (
                  <ProjectNotes
                    projectData={projectData}
                    headers={headers}
                    extras={extras}
                    customerId={customerId}
                    customer={customer}
                    noteGrant={noteGrant}
                  />
                )}
                {activeTab === "document" && (
                  <ProjectDocument
                    id={projectData?.id}
                    moduleType="project"
                    docGrant={docGrant}
                  />
                )}
              </Offcanvas.Body>
            </>
          )}
        </Offcanvas>
      </>

      <TasksOffcanvas
        taskShow={taskShow}
        onClose={() => setTaskShow(false)}
        setAddIndivisualTask={setAddIndivisualTask}
        addIndivisbleTask={addIndivisbleTask}
        setEditTaskData={setEditTaskData}
        editTaskData={editTaskData}
        setProjectId={setProjectId}
        projectId={projectId}
        fetchStages={fetchStages}
        setFetchTaskToken={setFetchTaskToken}
        fetchDetailsProjects={fetchDetailsProjects}
        refetch={refetch}
        moduleName={moduleName}
      />
      <TaskDetailModal
        show={taskDetailShow}
        onClose={() => setTaskDetailShow(false)}
        task_id={selectedTaskDetail?.id}
        projectData={projectData}
        headers={headers}
        extras={extras}
        customerId={customerId}
        assigneeOptions={[]}
        tagOptions={[]}
        taskGrant={taskGrant}
        onUpdated={() => {
          fetchDetailsProjects();
          fetchStages();
        }}
      />
    </>
  );
};

const styles = {
  listButtonsStyles: {
    fontSize: "13px",
    height: "30px",
    width: "auto",
    gap: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "2px",
    color: "#fff",
  },
  listButtonsStyless: {
    fontSize: "13px",
    height: "30px",
    width: "30px",
    gap: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "2px",
    color: "#fff",
  },
  navButtonsStyles: {
    fontSize: "15px",
    height: "34px",
    minWidth: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "2px",
  },
  iconStyles: {
    fontSize: 13,
    color: "#fff",
  },
  bottomP: {
    fontSize: 11,
  },
  bottomH4: {
    marginBottom: 2,
  },
  buttons: {
    color: "#fff",
    fontSize: "13px",
    height: "34px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#007bff",
    border: "1px solid #007bff",
  },
  filterbuttons: {
    fontSize: "13px",
    height: "34px",
    width: "34px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8f9fa",
    border: "1px solid #dee2e6",
  },
  ulStyles: {
    display: "flex",
    alignItems: "center",
    gap: "3px",
    justifyContent: "space-between",
    width: "100%",
  },
  emptyDivs: {
    borderRight: "1px solid #E0E4F8",
    height: "35px",
    margin: "0px 2px",
  },
  DropdownItemsStyle: {
    fontSize: ".93re",
    letterSpacing: ".0386rem",
    fontWeight: 400,
    color: "#2e384d",
  },
  navLinksStyle: {
    borderRadius: "0px",
    borderBottom: "1px solid #E0E4F8",
    borderRight: "1px solid #E0E4F8",
    fontSize: 14,
    padding: "11px",
    fontFamily: '"Inter", sans-serif !important',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  navLinksStyleLast: {
    borderRadius: "0px",
    borderBottom: "1px solid #E0E4F8",
    borderRight: "none",
    fontSize: 14,
    padding: "11px",
    fontFamily: '"Inter", sans-serif !important',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  navLinksStyleLastLast: {
    borderRadius: "0px",
    borderBottom: "1px solid #E0E4F8",
    borderRight: "none",
    fontSize: 14,
    height: "99%",
    padding: "11px",
    fontFamily: '"Inter", sans-serif !important',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  inActiveNavLinksStyle: {
    borderRadius: "0px",
    borderBottom: "1px solid #E0E4F8",
    borderRight: "1px solid #E0E4F8",
    fontSize: 14,
    padding: "11px",
    fontFamily: '"Inter", sans-serif !important',
    fontWeight: 400,
    lineHeight: 1.5,
    pointerEvents: "none",
  },
  tabContainer: {
    border: "1px solid #E0E4F8",
    borderRadius: "4px",
  },
  stickyHeader: {
    paddingBottom: "11px",
    background: "#fff",
    top: "0px",
  },
  labels: {
    fontSize: "12px",
    color: "#B0BAC9",
    fontWeight: 400,
  },
  iconsStyles: {
    color: "#2656a8",
    border: "1px solid #e6e6e6",
    background: "#e9f1ff",
    padding: "9px",
    borderRadius: "50%",
    marginRight: "10px",
    width: "15px",
    height: "15px",
    fontSize: "13px",
  },
  values: {
    color: "#2e384d",
    fontWeight: 400,
    fontSize: ".8333rem",
    letterSpacing: "0.25px",
  },

  navLinks: {
    color: "#848484",
    fontWeight: 500,
    fontSize: 13,
    display: "flex",
    alignItems: "center",
  },
  taskTitles: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    display: "inline-block",
  },
};